import { pushAPI } from '@/services/api';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      if (window.firebase) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

async function loadFirebase() {
  await loadScript('https://www.gstatic.com/firebasejs/10.12.5/firebase-app-compat.js');
  await loadScript('https://www.gstatic.com/firebasejs/10.12.5/firebase-messaging-compat.js');
}

function maskValue(value) {
  const text = String(value || '');
  if (!text) return '';
  if (text.length <= 10) return `${text.slice(0, 2)}…${text.slice(-2)}`;
  return `${text.slice(0, 6)}…${text.slice(-6)}`;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function diagnoseNativePush(registration, vapidKey) {
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      console.info('[push] Native PushManager existing subscription found', {
        endpointHost: (() => {
          try {
            return new URL(existing.endpoint).hostname;
          } catch {
            return '';
          }
        })(),
      });
      return { ok: true, existing: true };
    }
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
    console.info('[push] Native PushManager subscription succeeded', {
      endpointHost: (() => {
        try {
          return new URL(subscription.endpoint).hostname;
        } catch {
          return '';
        }
      })(),
    });
    await subscription.unsubscribe().catch(() => undefined);
    return { ok: true, existing: false };
  } catch (error) {
    console.warn('[push] Native PushManager subscription failed', {
      name: error && error.name,
      code: error && error.code,
      message: error && error.message,
    });
    return { ok: false, error };
  }
}

export function supportsPushNotifications() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

export async function registerAppServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/service-worker.js');
  } catch (error) {
    console.warn('Service worker registration failed', error);
    return null;
  }
}

export async function enablePushNotifications() {
  if (!supportsPushNotifications()) {
    return { ok: false, reason: 'unsupported' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: permission };
  }

  const configRes = await fetch('/api/config/firebase');
  const payload = await configRes.json();
  console.info('[push] Firebase public config', {
    enabled: payload.enabled,
    projectId: payload.config && payload.config.projectId,
    messagingSenderId: payload.config && payload.config.messagingSenderId,
    appId: maskValue(payload.config && payload.config.appId),
    vapidKeyPresent: !!payload.vapidKey,
    vapidKeyLength: payload.vapidKey ? String(payload.vapidKey).length : 0,
    vapidKey: maskValue(payload.vapidKey),
  });
  if (!payload.enabled || !payload.vapidKey) {
    return { ok: false, reason: 'not_configured' };
  }

  await loadFirebase();
  if (!window.firebase.apps.length) {
    window.firebase.initializeApp(payload.config);
  }

  const registration = await registerAppServiceWorker();
  if (!registration) return { ok: false, reason: 'service_worker_failed' };
  console.info('[push] Service worker ready', {
    scope: registration.scope,
    active: !!registration.active,
    permission: Notification.permission,
    protocol: window.location.protocol,
    host: window.location.host,
  });

  const nativeDiagnostic = await diagnoseNativePush(registration, payload.vapidKey);
  if (!nativeDiagnostic.ok) {
    return { ok: false, reason: 'native_push_failed' };
  }

  const messaging = window.firebase.messaging();
  let token = '';
  try {
    token = await messaging.getToken({
      vapidKey: payload.vapidKey,
      serviceWorkerRegistration: registration,
    });
  } catch (error) {
    console.warn('Firebase push token registration failed', {
      name: error && error.name,
      code: error && error.code,
      message: error && error.message,
      stack: error && error.stack,
    });
    const message = String(error && (error.message || error.code) || '').toLowerCase();
    if (message.includes('abort') || message.includes('push service')) {
      return { ok: false, reason: 'push_service_error' };
    }
    if (message.includes('vapid') || message.includes('key')) {
      return { ok: false, reason: 'invalid_vapid_key' };
    }
    return { ok: false, reason: 'token_failed' };
  }

  if (!token) return { ok: false, reason: 'no_token' };
  await pushAPI.subscribe({
    token,
    topic: 'general',
    platform: /iphone|ipad|ipod/i.test(navigator.userAgent) ? 'ios' : 'web',
  });
  localStorage.setItem('pushNotificationsEnabled', 'true');
  return { ok: true, token };
}
